import MagneticButton from "../components/anim/MagneticButton";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { V4_COMPANY } from "../data/siteV4";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Contact = () => {
    const navigate = useNavigate();

    // Main form state integrated with UI
    const [sending, setSending] = useState(false);
    const [utmSource, setUtmSource] = useState("google");
    const [formError, setFormError] = useState({});
    const generateCaptcha = () => {
        return Math.random()
            .toString(36)
            .substring(2, 8);
    };

    const [captchaText, setCaptchaText] = useState(generateCaptcha());

    const [captchaInput, setCaptchaInput] = useState("");

    const currentDate = new Date()
        .toLocaleDateString("en-GB")
        .replace(/-/g, "/");

    const initialValues = {
        name: "",
        email: "",
        subject: "",
        message: "",
        date: currentDate,
        leadSource: utmSource,
    };

    const [data, setData] = useState(initialValues);

    // UTM Source Setup
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const utm_source = urlParams.get("utm_source") || "google";

        setUtmSource(utm_source);

        setData((prevData) => ({
            ...prevData,
            leadSource: utm_source,
        }));
    }, []);

    useEffect(() => {
        fetch("https://api.ipify.org?format=json")
            .then((res) => res.json())
            .then((ipData) => {
                setData((prev) => ({
                    ...prev,
                    ipAddress: ipData.ip,
                }));
            })
            .catch(() => {
                console.log("Failed to fetch IP");
            });
    }, []);

    // Form Validation
    const validate = (values) => {
        const error = {};

        if (!values.name) error.name = "Name is required";

        if (!values.email) {
            error.email = "Email is required";
        } else {
            const emailRegex =
                /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

            if (!emailRegex.test(values.email)) {
                error.email = "Invalid email";
            }
        }

        if (!values.subject) error.subject = "Subject is required";

        if (captchaInput !== captchaText) {
            error.captcha = "Invalid captcha";
        }

        return error;
    };

    // Send Mail Function
    const sendMail = (e) => {
        e.preventDefault();

        const errors = validate(data);
        setFormError(errors);

        if (Object.keys(errors).length > 0) return;

        setSending(true);

        const newData = {
            ...data,
            date: currentDate,
            leadSource: utmSource,
        };

        const endpoint = process.env.REACT_APP_ENDPOINT_MAIL_URL;

        const mailURL = `${window.location.origin}${endpoint}${process.env.REACT_APP_SEND_MAIL_TOKEN
                ? `${endpoint.includes("?") ? "&" : "?"}token=${encodeURIComponent(
                    process.env.REACT_APP_SEND_MAIL_TOKEN
                )}`
                : ""
            }`;

        fetch(mailURL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(newData),
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Request failed");
                }

                return res.json();
            })
            .then(() => {
                setData(initialValues);
                setCaptchaInput("");
                navigate("/thank-you");
            })
            .catch((error) => {
                console.log(error);
                alert("Something went wrong please try again");
            })
            .finally(() => {
                setSending(false);
            });
    };

    // Input Change Handler
    const change = (e) => {
        const { name, value } = e.target;

        setData({
            ...data,
            [name]: value,
            leadSource: utmSource,
        });
    };

    return (
        <section
            id="contact"
            data-testid="v4-contact"
            className=" scroll-mt-28 relative py-24 md:py-32 bg-white overflow-hidden"
        >
            <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />

            <div className="absolute -top-40 -right-20 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                <div className="max-w-3xl mb-14">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
                        <span className="w-8 h-px bg-amber-500" />
                        Contact
                    </div>

                    <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-navy-900 tracking-[-0.03em] leading-[1]">
                        Let's build
                        <br />
                        <span className="text-amber-500">together.</span>
                    </h2>

                    <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-xl">
                        Tell us about your project. Our engineering team
                        typically replies within 24 hours.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5 space-y-5">
                        {[
                            {
                                icon: Mail,
                                label: "Email",
                                value: V4_COMPANY.email,
                                href: `mailto:${V4_COMPANY.email}`,
                            },
                            {
                                icon: Phone,
                                label: "Call",
                                value: V4_COMPANY.phone,
                                href: `tel:${V4_COMPANY.phone}`,
                            },
                            {
                                icon: MapPin,
                                label: "Visit",
                                value: V4_COMPANY.address,
                                href: null,
                            },
                        ].map((c, i) => (
                            <div
                                key={i}
                                className="lift-card p-7 rounded-2xl border border-slate-200 bg-white"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                                        <c.icon className="w-5 h-5" />
                                    </div>

                                    <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold">
                                        {c.label}
                                    </div>
                                </div>

                                {c.href ? (
                                    <a
                                        href={c.href}
                                        className="text-navy-900 font-display text-lg font-bold tracking-tight break-all"
                                    >
                                        {c.value}
                                    </a>
                                ) : (
                                    <p className="text-navy-900 font-display text-lg font-bold tracking-tight leading-snug">
                                        {c.value}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            onSubmit={sendMail}
                            data-testid="v4-contact-form"
                            className="rounded-3xl border border-slate-200 bg-white p-7 md:p-10 shadow-[0_8px_40px_-12px_rgba(10,37,64,0.1)]"
                        >
                            <h3 className="font-display text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">
                                Start a conversation
                            </h3>

                            <p className="mt-2 text-sm text-slate-600">
                                Share a few details — we'll route it to the
                                right engineer.
                            </p>

                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {[
                                    {
                                        k: "name",
                                        label: "Full Name *",
                                        type: "text",
                                        ph: "Enter your Name",
                                        col: 1,
                                        req: true,
                                    },
                                    {
                                        k: "email",
                                        label: "Email *",
                                        type: "email",
                                        ph: "Enter tour Email Address",
                                        col: 1,
                                        req: true,
                                    },
                                    {
                                        k: "subject",
                                        label: "Subject *",
                                        type: "text",
                                        ph: "EV charger manufacturing",
                                        col: 2,
                                        req: true,
                                    },
                                ].map((f) => (
                                    <div
                                        key={f.k}
                                        className={
                                            f.col === 2
                                                ? "sm:col-span-2"
                                                : ""
                                        }
                                    >
                                        <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                                            {f.label}
                                        </label>

                                        <input
                                            required={f.req}
                                            type={f.type}
                                            name={f.k}
                                            data-testid={`v4-input-${f.k}`}
                                            value={data[f.k]}
                                            onChange={change}
                                            className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 text-navy-900 placeholder:text-slate-400 transition-colors"
                                            placeholder={f.ph}
                                        />

                                        {formError[f.k] && (
                                            <p className="text-red-500 text-xs mt-1">
                                                {formError[f.k]}
                                            </p>
                                        )}
                                    </div>
                                ))}

                                <div className="sm:col-span-2">
                                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                                        Message
                                    </label>

                                    <textarea
                                        rows={5}
                                        name="message"
                                        data-testid="v4-input-message"
                                        value={data.message}
                                        onChange={change}
                                        className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 text-navy-900 placeholder:text-slate-400 transition-colors resize-none"
                                        placeholder="Tell us about your project..."
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-2 mt-1">
                                <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                                    Captcha *
                                </label>

                                <div className="mt-3 flex items-center gap-3 flex-nowrap">
                                    <input
                                        type="text"
                                        placeholder="Enter Captcha"
                                        value={captchaInput}
                                        onChange={(e) =>
                                            setCaptchaInput(e.target.value)
                                        }
                                        className="w-[180px] max-sm:w-[150px] bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-2 text-navy-900 placeholder:text-slate-400 transition-colors"
                                    />

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCaptchaText(generateCaptcha());
                                            setCaptchaInput("");
                                        }}
                                        className="text-2xl text-navy-900 shrink-0"
                                    >
                                        ↻
                                    </button>

                                    <div className="text-[1.25rem] tracking-[0.25em] text-navy-900 font-semibold italic select-none whitespace-nowrap">
                                        {captchaText}
                                    </div>
                                </div>

                                {formError.captcha && (
                                    <p className="text-red-500 text-xs mt-2">
                                        {formError.captcha}
                                    </p>
                                )}
                            </div>

                            <MagneticButton
                                strength={0.35}
                                className="mt-10 inline-block"
                            >
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="v4-submit"
                                    className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3 disabled:opacity-60"
                                >
                                    {sending
                                        ? "Sending..."
                                        : "Send Message"}

                                    <Send className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </button>
                            </MagneticButton>

                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;